import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";
import { useNavigate, useLocation } from "react-router-dom";
import apiClient from "../api/client";
import { toILS, computeWeightedAvgRate, computeTotalDebtILS } from "../utils/loanMetrics";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { can } from "../utils/permissions";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

ModuleRegistry.registerModules([AllCommunityModule]);

const SUMMARY_ROW_ID = "__summary__";

const CLASSIFICATION_LABELS = {
  INCOME_REAL_ESTATE: "נדל״ן מניב",
  CONSTRUCTION_FINANCE: "ליווי בנייה",
  LAND_LOAN: "הלוואת קרקע",
  BUSINESS_CREDIT: "אשראי עסקי",
  LOAN: "הלוואה",
  CREDIT_FACILITY: "מסגרת אשראי",
  OTHER: "אחר",
};

const STATUS_FILTERS = [
  { key: "active", label: "פעיל" },
  { key: "all", label: "הכל" },
  { key: "repaid", label: "נפרע" },
];

const DEFAULT_COLUMN_IDS = [
  "lender", "borrowerCompany", "basketName", "classification",
  "remainingPrincipal", "baseRateName", "interestRate", "maturityDate",
];

const ALL_COLUMNS_CONFIG = [
  { field: "lender", headerName: "מלווה", type: "text" },
  { field: "borrowerCompany", headerName: "לווה", type: "text" },
  { field: "basketName", headerName: "מסגרת", type: "text" },
  { field: "classification", headerName: "סיווג", type: "text" },
  { field: "classificationOtherReason", headerName: "סיבת אחר", type: "text" },
  { field: "loanType", headerName: "סוג הלוואה", type: "text" },
  { field: "paymentPeriod", headerName: "תקופת תשלום", type: "text" },
  { field: "projectName", headerName: "פרויקט", type: "text" },
  { field: "currency", headerName: "מטבע", type: "text" },
  { field: "principal", headerName: "קרן (M)", type: "number" },
  { field: "remainingPrincipal", headerName: "יתרת קרן (M)", type: "number" },
  { field: "originationDate", headerName: "תאריך העמדה", type: "date" },
  { field: "originationFeePct", headerName: "עמלת הקצאה %", type: "pct" },
  { field: "unusedCommitmentFeePct", headerName: "עמלת מסגרת %", type: "pct" },
  { field: "prepaymentPenaltyPct", headerName: "קנס פירעון %", type: "pct" },
  { field: "baseRateName", headerName: "בסיס ריבית", type: "text" },
  { field: "rateType", headerName: "סוג ריבית", type: "text" },
  { field: "margin", headerName: "מרווח %", type: "pct" },
  { field: "interestRate", headerName: "ריבית %", type: "pct" },
  { field: "maturityDate", headerName: "תאריך פירעון", type: "date" },
  { field: "notes", headerName: "הערות", type: "text" },
  { field: "status", headerName: "סטטוס", type: "text" },
  { field: "createdAt", headerName: "נוצר", type: "date" },
];

const COL_BY_FIELD = Object.fromEntries(ALL_COLUMNS_CONFIG.map((c) => [c.field, c]));

const CURRENCY_SYMBOLS = { ILS: "₪", USD: "$", EUR: "€", GBP: "£", JPY: "¥", CHF: "₣" };


// ── Formatters ──────────────────────────────────────────────

function formatM(value) {
  if (value == null || value === "") return "";
  const num = Number(value);
  if (Number.isNaN(num)) return "";
  const abs = Math.abs(num);
  if (abs >= 1000000) return `${parseFloat((num / 1000000).toFixed(1))}M`;
  if (abs >= 1000) return `${parseFloat((num / 1000).toFixed(0))}K`;
  return num.toLocaleString("he-IL");
}

function formatPct(value) {
  if (value == null || value === "") return "";
  const num = Number(value);
  if (Number.isNaN(num)) return "";
  return `${num}%`;
}

function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
}

function formatField(fieldId, value) {
  const config = COL_BY_FIELD[fieldId];
  if (!config || !value) return "";
  if (config.type === "number") return formatM(value);
  if (config.type === "pct") return formatPct(value);
  if (config.type === "date") return formatDate(value);
  if (fieldId === "classification") return CLASSIFICATION_LABELS[value] || value;
  return String(value);
}

function excelSafeText(value) {
  if (value == null) return "";
  const text = String(value);
  // Prevent spreadsheet formula injection when a user-entered value starts with
  // a formula character. Excel keeps the leading apostrophe out of the display.
  return /^[=+\-@]/.test(text) ? `'${text}` : text;
}

function excelDate(value) {
  if (!value) return null;
  // Date-only values need local construction; new Date("YYYY-MM-DD") shifts
  // them back one day in time zones west of UTC.
  const dateOnly = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnly) return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function columnLetter(index) {
  let result = "";
  let n = index;
  while (n > 0) {
    const remainder = (n - 1) % 26;
    result = String.fromCharCode(65 + remainder) + result;
    n = Math.floor((n - 1) / 26);
  }
  return result;
}

function getDaysToMaturity(maturityDate) {
  if (!maturityDate) return null;
  const d = new Date(maturityDate);
  if (Number.isNaN(d.getTime())) return null;
  return Math.round((d - Date.now()) / 86400000);
}

function getRiskLevel(loan) {
  if (loan.status === "repaid") return null;
  const days = getDaysToMaturity(loan.maturityDate);
  if (days === null) return null;
  if (days < 0) return "overdue";
  if (days <= 30) return "high";
  if (days <= 60) return "medium";
  return null;
}

// ── Risk dot renderer ────────────────────────────────────────

function RiskDotRenderer(params) {
  const [pos, setPos] = useState(null);
  if (params.data?.__isSummary) return null;
  const risk = getRiskLevel(params.data);
  if (!risk) return null;
  const colors = { overdue: "#ef4444", high: "#f97316", medium: "#f59e0b" };
  const labels = { overdue: "באיחור", high: "פירעון בתוך 30 יום", medium: "פירעון בתוך 60 יום" };
  const c = colors[risk];

  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
      <span
        style={{ width: 7, height: 7, borderRadius: "50%", background: c,
          display: "inline-block", boxShadow: `0 0 0 2px ${c}30`, cursor: "default" }}
        onMouseEnter={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          setPos({ top: r.top - 8, left: r.left + r.width / 2 });
        }}
        onMouseLeave={() => setPos(null)}
      />
      {pos && createPortal(
        <div style={{
          position: "fixed", top: pos.top, left: pos.left,
          transform: "translate(-50%, -100%)",
          background: "#1a1a1a", color: "#fff",
          padding: "5px 10px", borderRadius: "6px",
          fontSize: "12px", fontFamily: "inherit",
          whiteSpace: "nowrap", pointerEvents: "none",
          zIndex: 9999, boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
          direction: "rtl",
        }}>
          {labels[risk]}
        </div>,
        document.body
      )}
    </div>
  );
}

// ── Ghost column header ──────────────────────────────────────
// Shows the name of the "next" available column, grayed out.
// Clicking it immediately adds that column to the table.

function GhostHeaderComp(props) {
  const ctx = props.context;
  const ghostFieldId = ctx?.ghostFieldId;
  if (!ghostFieldId) return null;
  const label = COL_BY_FIELD[ghostFieldId]?.headerName ?? ghostFieldId;
  return (
    <div
      className="ghost-col-header"
      onClick={(e) => { e.stopPropagation(); ctx?.onAddColumn?.(ghostFieldId); }}
      title={`לחץ להוספת עמודת "${label}"`}
    >
      <span className="ghost-col-label">{label}</span>
      <span className="ghost-col-hint">+ הוסף</span>
    </div>
  );
}

// ── "+" Add Column header button ─────────────────────────────
// Uses AG Grid context (via props.context) to get fresh state.
// This prevents stale closures and avoids remount issues.

function AddColumnHeaderComp(props) {
  const ctx = props.context;
  const [open, setOpen] = useState(false);
  const [snapshot, setSnapshot] = useState([]);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (!btnRef.current?.closest(".add-col-wrap")?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleToggle = (e) => {
    e.stopPropagation();
    if (!open) {
      setSnapshot(ctx?.availableToAdd || []);
      const rect = btnRef.current?.getBoundingClientRect();
      if (rect) setMenuPos({ top: rect.bottom + 4, left: rect.left });
    }
    setOpen((v) => !v);
  };

  if (!ctx?.availableToAdd?.length) return null;

  return (
    <div className="add-col-wrap">
      <button
        ref={btnRef}
        type="button"
        className="add-col-btn"
        onClick={handleToggle}
        title="הוסף עמודה"
      >
        +
      </button>
      {open && snapshot.length > 0 && createPortal(
        <div
          className="add-col-menu"
          style={{ position: "fixed", top: menuPos.top, left: menuPos.left, zIndex: 9999 }}
        >
          {snapshot.map((fid) => (
            <button
              key={fid}
              type="button"
              className="add-col-option"
              onMouseDown={(e) => {
                e.preventDefault();
                ctx?.onAddColumn?.(fid);
                setOpen(false);
              }}
            >
              {COL_BY_FIELD[fid]?.headerName ?? fid}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

// ── Repayment dialog ─────────────────────────────────────────
// Repaying a loan does not release its security by itself: the pledged shares stay
// sitting in the account, and only the operator knows whether the lien was actually
// lifted. So we ask, separately for pledges and guarantees, instead of guessing.
// A shared basket's security backs other loans, so it is never offered here.

function RepayDialog({ loan, onCancel, onConfirm }) {
  const [pledges, setPledges] = useState([]);
  const [guarantees, setGuarantees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [releasePledges, setReleasePledges] = useState(false);
  const [releaseGuarantees, setReleaseGuarantees] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const basketId = loan.basket?._id || loan.basketId;
  const isShared = !!loan.basketName;

  useEffect(() => {
    let alive = true;
    if (!basketId || isShared) { setLoading(false); return undefined; }
    Promise.all([
      apiClient.get(`/pledges?basketId=${basketId}&status=ACTIVE`).then((r) => r.data || []).catch(() => []),
      apiClient.get(`/guarantees?basketId=${basketId}&status=ACTIVE`).then((r) => r.data || []).catch(() => []),
    ]).then(([p, g]) => {
      if (!alive) return;
      setPledges(p);
      setGuarantees(g);
      setLoading(false);
    });
    return () => { alive = false; };
  }, [basketId, isShared]);

  const submit = () => {
    setSubmitting(true);
    onConfirm({ releasePledges, releaseGuarantees });
  };

  return createPortal(
    <div className="repay-dialog-backdrop" onClick={onCancel}>
      <div className="repay-dialog" onClick={(e) => e.stopPropagation()}>
        <h3 className="repay-dialog-title">סיום הלוואה</h3>
        <p className="repay-dialog-sub">
          {loan.borrowerCompany || loan.lender} — ההלוואה תסומן כנפרעה.
        </p>

        {loading && <div className="repay-dialog-note">טוען בטחונות…</div>}

        {!loading && isShared && (
          <div className="repay-dialog-note">
            ההלוואה משויכת למסגרת «{loan.basketName}». הבטחונות שלה מגבים גם הלוואות אחרות
            ולכן לא ישוחררו כאן — לשחרור, היכנסו לדף הבטחונות.
          </div>
        )}

        {!loading && !isShared && pledges.length === 0 && guarantees.length === 0 && (
          <div className="repay-dialog-note">להלוואה זו אין בטחונות פעילים.</div>
        )}

        {!loading && !isShared && pledges.length > 0 && (
          <div className="repay-dialog-block">
            <div className="repay-dialog-question">
              האם הפירעון משחרר את השעבודים? ({pledges.length})
            </div>
            <ul className="repay-dialog-list">
              {pledges.map((p) => (
                <li key={p._id}>
                  {(p.assetId?.assetName) || "—"}
                  {p.assetId?.symbol ? ` · ${p.assetId.symbol}` : ""}
                  {" · "}
                  {p.pledgedUnits != null ? `${Number(p.pledgedUnits).toLocaleString("he-IL")} יח׳` : "נכס בשלמותו"}
                  {p.lienHolder ? ` · ${p.lienHolder}` : ""}
                </li>
              ))}
            </ul>
            <div className="repay-dialog-choices">
              <label>
                <input type="radio" name="pledges" checked={releasePledges}
                  onChange={() => setReleasePledges(true)} />
                כן — שחרר את השעבודים
              </label>
              <label>
                <input type="radio" name="pledges" checked={!releasePledges}
                  onChange={() => setReleasePledges(false)} />
                לא — השעבודים נשארים בתוקף
              </label>
            </div>
          </div>
        )}

        {!loading && !isShared && guarantees.length > 0 && (
          <div className="repay-dialog-block">
            <div className="repay-dialog-question">
              האם הפירעון משחרר את הערבויות? ({guarantees.length})
            </div>
            <ul className="repay-dialog-list">
              {guarantees.map((g) => (
                <li key={g._id}>
                  {g.guaranteeType === "PERSONAL" ? "אישית" : "תאגידית"}
                  {g.guarantorName ? ` · ${g.guarantorName}` : ""}
                  {g.amount != null ? ` · ₪${Number(g.amount).toLocaleString("he-IL")}` : ""}
                </li>
              ))}
            </ul>
            <div className="repay-dialog-choices">
              <label>
                <input type="radio" name="guarantees" checked={releaseGuarantees}
                  onChange={() => setReleaseGuarantees(true)} />
                כן — שחרר את הערבויות
              </label>
              <label>
                <input type="radio" name="guarantees" checked={!releaseGuarantees}
                  onChange={() => setReleaseGuarantees(false)} />
                לא — הערבויות נשארות בתוקף
              </label>
            </div>
          </div>
        )}

        <div className="repay-dialog-actions">
          <button type="button" className="repay-dialog-cancel" onClick={onCancel}>ביטול</button>
          <button type="button" className="repay-dialog-confirm" onClick={submit} disabled={loading || submitting}>
            סיום הלוואה
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Main component ───────────────────────────────────────────

export default function LoanGrid({ onUnauthorized, canEdit = false }) {
  const user = useCurrentUser();
  const canViewMetrics = can(user, 'loans.viewMetrics');
  const canExportCsv   = can(user, 'loans.exportCsv');
  const canExportPdf   = can(user, 'loans.exportPdf');
  const canCreateLoan  = can(user, 'loans.create');
  const canEditLoan    = can(user, 'loans.edit');
  const canCloseLoan   = can(user, 'loans.close');
  const canHardDelete  = can(user, 'loans.hardDelete');
  const canViewRisk    = can(user, 'loans.viewRisk');
  const [allLoans, setAllLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [repayTarget, setRepayTarget] = useState(null);
  const [statusFilter, setStatusFilter] = useState("active");
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get("lender") || "";
  });
  const [extraColumnIds, setExtraColumnIds] = useState([]);
  const [fxRates, setFxRates] = useState({}); // { USD: 2.991, EUR: 3.411, ... }
  const navigate = useNavigate();

  // Fetch BOI FX rates once on mount — no API key needed
  useEffect(() => {
    apiClient.get(`/api/rates/fx`)
      .then((res) => {
        const rates = {};
        Object.entries(res.data).forEach(([pair, doc]) => {
          const [from] = pair.split("/");
          // BOI sometimes quotes per N units (e.g. JPY per 100); rate is already per-unit from our service
          if (from && doc.rate) rates[from] = Number(doc.rate);
        });
        setFxRates(rates);
      })
      .catch(() => {}); // silently fail — amounts display in original currency
  }, []);

  const fetchLoans = useCallback(() => {
    setLoading(true);
    apiClient.get(`/loans`)
      .then((res) => setAllLoans(Array.isArray(res.data) ? res.data : []))
      .catch((err) => {
        if (err.response?.status === 401) { onUnauthorized?.(); navigate("/login", { replace: true }); return; }
        console.error(err);
      })
      .finally(() => setLoading(false));
  }, [navigate, onUnauthorized]);

  useEffect(() => { fetchLoans(); }, [fetchLoans]);

  const loans = useMemo(() => {
    let r = allLoans;
    if (statusFilter === "active") r = r.filter((l) => l.status !== "repaid");
    else if (statusFilter === "repaid") r = r.filter((l) => l.status === "repaid");
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      r = r.filter((l) =>
        (l.lender || "").toLowerCase().includes(q) ||
        (l.borrowerCompany || "").toLowerCase().includes(q) ||
        (l.projectName || "").toLowerCase().includes(q) ||
        (l.basketName || "").toLowerCase().includes(q)
      );
    }
    return r;
  }, [allLoans, statusFilter, searchQuery]);

  const counts = useMemo(() => ({
    all: allLoans.length,
    active: allLoans.filter((l) => l.status !== "repaid").length,
    repaid: allLoans.filter((l) => l.status === "repaid").length,
  }), [allLoans]);

  const visibleColumnIds = useMemo(() => [...DEFAULT_COLUMN_IDS, ...extraColumnIds], [extraColumnIds]);

  const availableToAdd = useMemo(
    () => ALL_COLUMNS_CONFIG.filter((c) => !visibleColumnIds.includes(c.field)).map((c) => c.field),
    [visibleColumnIds]
  );

  const metrics = useMemo(() => {
    const active = loans.filter((l) => l.status !== "repaid");
    // All amounts converted to ILS for aggregation
    const totalPrincipalILS = active.reduce((s, l) => s + toILS(l.principal, l.currency, fxRates), 0);
    const totalRemainingILS = computeTotalDebtILS(active, fxRates);
    const weightedRate = computeWeightedAvgRate(active, fxRates) ?? 0;
    const weightedDays = totalPrincipalILS > 0
      ? active.reduce((s, l) => {
          const d = getDaysToMaturity(l.maturityDate);
          return s + (d !== null ? d : 0) * toILS(l.principal, l.currency, fxRates);
        }, 0) / totalPrincipalILS : 0;
    const foreignCurrencies = [...new Set(active.map((l) => l.currency).filter((c) => c && c !== "ILS"))];
    return { totalRemainingILS, totalPrincipalILS, weightedRate, weightedDays, foreignCurrencies };
  }, [loans, fxRates]);

  const pinnedBottomRowData = useMemo(() => {
    const row = { __isSummary: true, _id: SUMMARY_ROW_ID, currency: "ILS" };
    visibleColumnIds.forEach((fieldId, i) => {
      if (i === 0) row[fieldId] = "סה״כ (₪)";
      else if (fieldId === "remainingPrincipal") row[fieldId] = metrics.totalRemainingILS;
      else if (fieldId === "principal") row[fieldId] = metrics.totalPrincipalILS;
      else if (fieldId === "interestRate") row[fieldId] = metrics.weightedRate;
      else if (fieldId === "maturityDate" && metrics.weightedDays > 0)
        row[fieldId] = new Date(Date.now() + metrics.weightedDays * 86400000).toISOString();
      else if (fieldId === "status") row[fieldId] = loans.length ? `${loans.length} הלוואות` : "";
      else row[fieldId] = "";
    });
    return [row];
  }, [visibleColumnIds, metrics, loans]);

  const handleEdit = useCallback((id) => navigate(`/edit/${id}`), [navigate]);

  const handleEnd = useCallback((id) => {
    const loan = allLoans.find((l) => String(l._id) === String(id));
    if (loan) setRepayTarget(loan);
  }, [allLoans]);

  const confirmRepay = useCallback((flags) => {
    if (!repayTarget) return;
    apiClient.delete(`/loans/${repayTarget._id}`, { data: flags })
      .then(() => { setRepayTarget(null); fetchLoans(); })
      .catch((err) => {
        console.error(err);
        setRepayTarget(null);
        window.alert(err?.response?.data?.error || "סיום ההלוואה נכשל");
      });
  }, [repayTarget, fetchLoans]);

  const handleDelete = useCallback((id) => {
    if (!window.confirm("למחוק את ההלוואה לצמיתות?")) return;
    apiClient.delete(`/loans/${id}/hard`).then(fetchLoans).catch(console.error);
  }, [fetchLoans]);

  const handleAddColumn = useCallback((fieldId) => {
    if (fieldId) setExtraColumnIds((prev) => [...prev, fieldId]);
  }, []);

  const handleRemoveExtraColumn = useCallback((fieldId) => {
    if (fieldId) setExtraColumnIds((prev) => prev.filter((id) => id !== fieldId));
  }, []);

  // Stable context ref — mutated in place so AG Grid always reads fresh values
  // without needing to rebuild columnDefs when availableToAdd changes.
  const gridCtxRef = useRef({ availableToAdd: [], ghostFieldId: null, onAddColumn: null });
  gridCtxRef.current.availableToAdd = availableToAdd;
  gridCtxRef.current.ghostFieldId = availableToAdd[0] ?? null;
  gridCtxRef.current.onAddColumn = handleAddColumn;

  // columnDefs does NOT depend on availableToAdd — state is passed via context.
  const columnDefs = useMemo(() => {
    const riskCol = {
      field: "__risk", headerName: "",
      width: 28, maxWidth: 28, flex: 0,
      sortable: false, filter: false, resizable: false, suppressSizeToFit: true,
      cellRenderer: RiskDotRenderer,
    };
    const riskCols = canViewRisk ? [riskCol] : [];

    // Per-field minimum widths — prevents Hebrew text clipping
    const FIELD_MIN_WIDTHS = {
      lender: 140, borrowerCompany: 140,
      projectName: 130, notes: 180, classificationOtherReason: 140,
      classification: 130, maturityDate: 120, originationDate: 120,
    };
    const TYPE_MIN_WIDTHS = { number: 110, pct: 100, date: 120, text: 115 };

    const dataCols = visibleColumnIds.map((fieldId) => {
      const config = COL_BY_FIELD[fieldId] || { field: fieldId, headerName: fieldId, type: "text" };
      const minWidth = FIELD_MIN_WIDTHS[fieldId] ?? TYPE_MIN_WIDTHS[config.type] ?? 115;
      const def = { field: config.field, headerName: config.headerName, flex: 1, minWidth };
      if (config.type === "number") {
        def.valueFormatter = (p) => {
          // Summary row: always ILS (already converted)
          if (p.node?.rowPinned === "bottom") return `₪${formatM(p.value)}`;
          const cur = p.data?.currency || "ILS";
          const sym = CURRENCY_SYMBOLS[cur] ?? `${cur} `;
          return `${sym}${formatM(p.value)}`;
        };
      } else if (config.type === "pct") {
        def.valueFormatter = (p) =>
          p.node?.rowPinned === "bottom" && p.value != null
            ? `${Number(p.value).toFixed(2)}%` : formatPct(p.value);
      } else if (config.type === "date") {
        def.valueFormatter = (p) => formatDate(p.value);
      } else if (config.field === "classification") {
        def.valueFormatter = (p) => CLASSIFICATION_LABELS[p.value] || p.value || "";
      }
      return def;
    });

    const actionsCol = {
      headerName: "פעולות", field: "actions",
      width: (canEditLoan || canCloseLoan || canHardDelete) ? 200 : 0, flex: 0, suppressSizeToFit: true, sortable: false, filter: false,
      cellRenderer: (p) => {
        if (p.data?.__isSummary) return null;
        const id = p.data._id;
        return (
          <div style={{ display: "flex", gap: 6, alignItems: "center", height: "100%" }}
            onClick={(e) => e.stopPropagation()}>
            {canEditLoan && (
              <button type="button" className="ag-grid-action-btn"
                onClick={(e) => { e.stopPropagation(); handleEdit(id); }}>עריכה</button>
            )}
            {canCloseLoan && (
              <button type="button" className="ag-grid-action-btn"
                onClick={(e) => { e.stopPropagation(); handleEnd(id); }}>סיום</button>
            )}
            {canHardDelete && (
              <button type="button" className="ag-grid-action-btn ag-grid-action-btn-danger"
                onClick={(e) => { e.stopPropagation(); handleDelete(id); }}>מחיקה</button>
            )}
          </div>
        );
      },
    };

    // Ghost column — shows first available column's data at low opacity.
    // Uses context for fresh ghostFieldId (doesn't cause remount).
    const ghostCol = {
      field: "__ghost", headerName: "",
      flex: 1, minWidth: 160, maxWidth: 260,
      sortable: false, filter: false, resizable: false,
      headerClass: "ag-ghost-header",
      cellClass: "ag-ghost-cell",
      headerComponent: GhostHeaderComp,
      valueGetter: (p) => {
        if (!p.context?.ghostFieldId || p.data?.__isSummary) return null;
        return p.data?.[p.context.ghostFieldId];
      },
      cellRenderer: (p) => {
        if (p.data?.__isSummary) return null;
        const fid = p.context?.ghostFieldId;
        if (!fid) return null;
        const val = p.data?.[fid];
        const formatted = formatField(fid, val);
        return (
          <span
            className="ghost-cell-content"
            onClick={(e) => { e.stopPropagation(); p.context?.onAddColumn?.(fid); }}
            title={`לחץ להוספת "${COL_BY_FIELD[fid]?.headerName}"`}
          >
            {formatted || "—"}
          </span>
        );
      },
    };

    // "+" add-column button — uses context, not headerComponentParams
    const addCol = {
      field: "__add_col", headerName: "",
      width: 42, flex: 0,
      sortable: false, filter: false, resizable: false, suppressSizeToFit: true,
      headerComponent: AddColumnHeaderComp,
      cellRenderer: () => null,
    };

    return [...riskCols, ...dataCols, actionsCol, ghostCol, addCol];
  }, [visibleColumnIds, handleEdit, handleEnd, handleDelete, canEdit, canEditLoan, canCloseLoan, canHardDelete, canViewRisk]);

  // ── Export ───────────────────────────────────────────────────

  const buildExportRows = () => {
    const headers = visibleColumnIds.map((f) => COL_BY_FIELD[f]?.headerName ?? f);
    const dataRows = loans.map((loan) =>
      visibleColumnIds.map((fieldId) => {
        const config = COL_BY_FIELD[fieldId];
        const raw = loan[fieldId];
        if (raw == null || raw === "") return "";
        if (config?.type === "number") {
          const value = Number(raw);
          return Number.isFinite(value) ? value : "";
        }
        if (config?.type === "pct") {
          const value = Number(raw);
          return Number.isFinite(value) ? value : "";
        }
        if (config?.type === "date") return excelDate(raw) || "";
        if (fieldId === "classification") return excelSafeText(CLASSIFICATION_LABELS[raw] || raw);
        return excelSafeText(raw);
      })
    );
    const displayRows = loans.map((loan) =>
      visibleColumnIds.map((fieldId) => formatField(fieldId, loan[fieldId]))
    );
    return { headers, dataRows, displayRows };
  };

  const handleExportExcel = async () => {
    try {
    // ExcelJS is loaded only when an export is requested, keeping the main
    // loans-page bundle light for everyone who does not use this action.
    const { default: ExcelJS } = await import("exceljs");
    const { headers, dataRows } = buildExportRows();
    const exportDate = new Date();
    const columnCount = visibleColumnIds.length;
    const lastColumn = columnLetter(columnCount);
    const headerRowNumber = 6;
    const firstDataRow = headerRowNumber + 1;
    const lastDataRow = firstDataRow + dataRows.length - 1;
    const selectedStatus = STATUS_FILTERS.find((item) => item.key === statusFilter)?.label || statusFilter;
    const selectedTotalILS = computeTotalDebtILS(loans, fxRates);
    const activeLoans = loans.filter((loan) => loan.status !== "repaid");
    const activeDebtILS = computeTotalDebtILS(activeLoans, fxRates);
    const selectedWeightedRate = computeWeightedAvgRate(activeLoans, fxRates);
    const summaryValues = visibleColumnIds.map((fieldId, index) => {
      if (index === 0) return "סה״כ יתרת חוב פעילה (₪)";
      if (fieldId === "remainingPrincipal") return activeDebtILS;
      if (fieldId === "interestRate") return selectedWeightedRate ?? "";
      if (fieldId === "status") return `${loans.length} הלוואות`;
      return "";
    });
    const displayedTextLength = (value, config) => {
      if (value == null || value === "") return 0;
      if (config?.type === "date") return 10; // dd/mm/yyyy
      if (config?.type === "number") return Number(value).toLocaleString("en-US", {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
      }).length;
      if (config?.type === "pct") return `${Number(value).toFixed(2)}%`.length;
      return Math.max(...String(value).split(/\r?\n/).map((line) => line.length));
    };
    const columnWidths = visibleColumnIds.map((fieldId, index) => {
      const config = COL_BY_FIELD[fieldId];
      const longestValue = Math.max(
        headers[index].length,
        displayedTextLength(summaryValues[index], config),
        ...dataRows.map((row) => displayedTextLength(row[index], config))
      );
      // Excel's maximum column width is 255 characters. All shorter values
      // are given their full displayed width, plus a small amount of padding.
      return Math.min(255, Math.max(12, longestValue + 3));
    });

    const workbook = new ExcelJS.Workbook();
    workbook.creator = "מערכת הלוואות";
    workbook.created = exportDate;
    workbook.modified = exportDate;
    workbook.properties.date1904 = false;

    const worksheet = workbook.addWorksheet("הלוואות", {
      views: [{ rightToLeft: true, state: "frozen", ySplit: headerRowNumber }],
      pageSetup: {
        orientation: "landscape",
        paperSize: 9,
        fitToPage: true,
        fitToWidth: 1,
        fitToHeight: 0,
        margins: { left: 0.25, right: 0.25, top: 0.5, bottom: 0.5, header: 0.2, footer: 0.2 },
      },
      properties: { defaultRowHeight: 20 },
    });

    worksheet.columns = visibleColumnIds.map((fieldId, index) => ({
      key: fieldId,
      width: columnWidths[index],
    }));

    const titleFill = "17365D";
    const sectionFill = "DCE6F1";
    const headerFill = "1F4E78";
    const border = { style: "thin", color: { argb: "D9E2F3" } };
    const allBorders = { top: border, left: border, bottom: border, right: border };

    worksheet.mergeCells(`A1:${lastColumn}1`);
    const titleCell = worksheet.getCell("A1");
    titleCell.value = "דוח הלוואות";
    titleCell.font = { name: "Aptos", size: 18, bold: true, color: { argb: "FFFFFFFF" } };
    titleCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: titleFill } };
    titleCell.alignment = { horizontal: "center", vertical: "middle" };
    worksheet.getRow(1).height = 30;

    worksheet.getCell("A2").value = "סטטוס מוצג";
    worksheet.getCell("B2").value = selectedStatus;
    worksheet.getCell("C2").value = "מספר הלוואות";
    worksheet.getCell("D2").value = loans.length;
    worksheet.getCell("E2").value = "נוצר בתאריך";
    worksheet.getCell("F2").value = exportDate;
    worksheet.getCell("F2").numFmt = "dd/mm/yyyy hh:mm";
    worksheet.getCell("A3").value = "סה״כ יתרת חוב (₪)";
    worksheet.getCell("B3").value = selectedTotalILS;
    worksheet.getCell("B3").numFmt = '#,##0.00;[Red]-#,##0.00;–';
    worksheet.getCell("C3").value = "ריבית משוקללת";
    worksheet.getCell("D3").value = selectedWeightedRate ?? "—";
    if (selectedWeightedRate != null) worksheet.getCell("D3").numFmt = '0.00"%"';

    ["A2", "C2", "E2", "A3", "C3"].forEach((address) => {
      const cell = worksheet.getCell(address);
      cell.font = { name: "Aptos", bold: true, color: { argb: "17365D" } };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: sectionFill } };
      cell.alignment = { horizontal: "right", vertical: "middle" };
      cell.border = allBorders;
    });
    ["B2", "D2", "F2", "B3", "D3"].forEach((address) => {
      const cell = worksheet.getCell(address);
      cell.font = { name: "Aptos", color: { argb: "1F1F1F" } };
      cell.alignment = { horizontal: "right", vertical: "middle" };
      cell.border = allBorders;
    });

    const headerRow = worksheet.getRow(headerRowNumber);
    headerRow.values = headers;
    headerRow.height = 27;
    headerRow.eachCell((cell) => {
      cell.font = { name: "Aptos", bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: headerFill } };
      cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
      cell.border = allBorders;
    });

    dataRows.forEach((values, index) => {
      const row = worksheet.addRow(values);
      const loan = loans[index];
      const rowFill = index % 2 === 0 ? "F7FAFC" : "FFFFFFFF";
      row.height = 22;
      row.eachCell({ includeEmpty: true }, (cell, columnNumber) => {
        const config = COL_BY_FIELD[visibleColumnIds[columnNumber - 1]];
        cell.font = { name: "Aptos", color: { argb: "1F1F1F" } };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: rowFill } };
        cell.border = allBorders;
        cell.alignment = { horizontal: "right", vertical: "middle", wrapText: false };
        if (config?.type === "number") cell.numFmt = '#,##0.00;[Red]-#,##0.00;–';
        if (config?.type === "pct") cell.numFmt = '0.00"%"';
        if (config?.type === "date") cell.numFmt = "dd/mm/yyyy";
      });

      const risk = getRiskLevel(loan);
      const maturityColumn = visibleColumnIds.indexOf("maturityDate") + 1;
      if (risk && maturityColumn > 0) {
        const riskColors = { overdue: "FDE9E7", high: "FCE4D6", medium: "FFF2CC" };
        const cell = row.getCell(maturityColumn);
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: riskColors[risk] } };
        cell.font = { name: "Aptos", bold: true, color: { argb: risk === "overdue" ? "C00000" : "7F6000" } };
      }
    });

    const summaryRow = worksheet.addRow(summaryValues);
    summaryRow.height = 24;
    summaryRow.eachCell({ includeEmpty: true }, (cell, columnNumber) => {
      const config = COL_BY_FIELD[visibleColumnIds[columnNumber - 1]];
      cell.font = { name: "Aptos", bold: true, color: { argb: "17365D" } };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "D9EAD3" } };
      cell.border = allBorders;
      cell.alignment = { horizontal: "right", vertical: "middle", wrapText: true };
      if (config?.type === "number") cell.numFmt = '#,##0.00;[Red]-#,##0.00;–';
      if (config?.type === "pct") cell.numFmt = '0.00"%"';
    });

    worksheet.autoFilter = { from: `A${headerRowNumber}`, to: `${lastColumn}${Math.max(lastDataRow, headerRowNumber)}` };
    worksheet.getRow(4).height = 8;
    worksheet.headerFooter.oddFooter = "&Lמערכת הלוואות&Rעמוד &P מתוך &N";

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    const dateForFile = exportDate.toISOString().slice(0, 10);
    anchor.href = url;
    anchor.setAttribute("download", `loans-${dateForFile}.xlsx`);
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Excel export failed", error);
      alert("לא ניתן ליצור את קובץ ה-Excel. נסה שוב.");
    }
  };

  const handleExportPdf = () => {
    const { headers, displayRows } = buildExportRows();
    const win = window.open("", "_blank");
    if (!win) { alert("חסימת פופ-אפים מונעת יצירת PDF."); return; }
    const escapeHtml = (value) => String(value ?? "")
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
    const tableHtml = [`<tr>${headers.map((h) => `<th>${escapeHtml(h)}</th>`).join("")}</tr>`,
      ...displayRows.map((r) => `<tr>${r.map((c) => `<td>${escapeHtml(c)}</td>`).join("")}</tr>`)].join("");
    win.document.write(`<html><head><meta charset="utf-8"><title>סיכום הלוואות</title>
      <style>@page{size:landscape;margin:10mm}body{font-family:sans-serif;direction:rtl}h2{text-align:center}
      table{border-collapse:collapse;width:100%}th,td{border:1px solid #ccc;padding:4px 6px;font-size:12px}
      th{background:#f0f0f0}</style></head><body><h2>מערכת הלוואות - סיכום</h2><table>${tableHtml}</table>
      <script>window.onload=function(){window.print()}</script></body></html>`);
    win.document.close();
  };

  // ── Render ───────────────────────────────────────────────────

  return (
    <div className="page-grid">

      <div className="gl-header">
        <h2 className="gl-title">הלוואות</h2>
        <div className="gl-header-actions">
          {canCreateLoan && (
            <button type="button" className="btn btn-primary btn-sm" onClick={() => navigate("/create")}>
              + הלוואה חדשה
            </button>
          )}
          {canExportCsv && <button type="button" className="btn btn-ghost btn-sm" onClick={handleExportExcel}>Excel ↓</button>}
          {canExportPdf && <button type="button" className="btn btn-ghost btn-sm" onClick={handleExportPdf}>PDF ↓</button>}
        </div>
      </div>

      {canViewMetrics && !loading && metrics.totalRemainingILS > 0 && (
        <div className="gl-metrics">
          <div className="gl-metric">
            <span className="gl-metric-value">₪{formatM(metrics.totalRemainingILS)}</span>
            <span className="gl-metric-label">סה״כ חוב{metrics.foreignCurrencies.length > 0 ? " (מומר לש״ח)" : ""}</span>
          </div>
          <div className="gl-metric-sep" />
          <div className="gl-metric">
            <span className="gl-metric-value">{metrics.weightedRate.toFixed(2)}%</span>
            <span className="gl-metric-label">ריבית ממוצעת</span>
          </div>
          <div className="gl-metric-sep" />
          <div className="gl-metric">
            <span className="gl-metric-value">
              {metrics.weightedDays > 0 ? Math.round(metrics.weightedDays) : "—"}
            </span>
            <span className="gl-metric-label">ימי פדיון ממוצע</span>
          </div>
          <div className="gl-metric-sep" />
          <div className="gl-metric">
            <span className="gl-metric-value">{loans.length}</span>
            <span className="gl-metric-label">הלוואות</span>
          </div>
          {metrics.foreignCurrencies.length > 0 && (
            <div className="gl-fx-note">
              {metrics.foreignCurrencies.map((cur) => fxRates[cur] ? (
                <span key={cur} className="gl-fx-rate">
                  {CURRENCY_SYMBOLS[cur] ?? cur}1 = ₪{fxRates[cur].toFixed(3)}
                </span>
              ) : null)}
              <span className="gl-fx-source">שע״ח בנק ישראל</span>
            </div>
          )}
        </div>
      )}

      <div className="gl-filter-bar">
        <div className="gl-pills">
          {STATUS_FILTERS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              className={`gl-pill${statusFilter === key ? " gl-pill-active" : ""}`}
              onClick={() => setStatusFilter(key)}
            >
              {label}
              <span className="gl-pill-count">{counts[key]}</span>
            </button>
          ))}
        </div>

        <div className="gl-search">
          <svg className="gl-search-icon" width="14" height="14" viewBox="0 0 16 16" fill="none">
            <circle cx="6.5" cy="6.5" r="5" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M10.5 10.5L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          <input
            type="text"
            className="gl-search-input"
            placeholder="חיפוש: מלווה, לווה, פרויקט..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button type="button" className="gl-search-clear" onClick={() => setSearchQuery("")}>×</button>
          )}
        </div>

        {extraColumnIds.length > 0 && (
          <div className="gl-extra-cols">
            {extraColumnIds.map((fid) => (
              <span key={fid} className="gl-extra-chip">
                {COL_BY_FIELD[fid]?.headerName ?? fid}
                <button type="button" className="gl-extra-chip-remove"
                  onClick={() => handleRemoveExtraColumn(fid)} title="הסר עמודה">×</button>
              </span>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <div className="grid-loading">טוען הלוואות...</div>
      ) : loans.length === 0 ? (
        <div className="gl-empty">
          <p>{searchQuery ? "לא נמצאו תוצאות לחיפוש זה." : "אין הלוואות להצגה."}</p>
          {searchQuery && (
            <button type="button" className="btn btn-ghost" onClick={() => setSearchQuery("")}>נקה חיפוש</button>
          )}
        </div>
      ) : (
        <div className="grid-wrapper">
          <div className="ag-theme-alpine ag-theme-premium">
            <AgGridReact
              theme="legacy"
              enableRtl={true}
              rowData={loans}
              pinnedBottomRowData={pinnedBottomRowData}
              context={gridCtxRef.current}
              getRowId={(p) => (p.data?.__isSummary ? SUMMARY_ROW_ID : p.data?._id)}
              getRowClass={(p) => (p.node?.rowPinned === "bottom" ? "ag-sum-row" : "")}
              columnDefs={columnDefs}
              defaultColDef={{
                resizable: true,
                sortable: true,
                filter: false,
                floatingFilter: false,
                flex: 1,
                minWidth: 90,
              }}
              domLayout="normal"
              onRowClicked={(p) => {
                // The action buttons (עריכה/סיום/מחיקה) live inside the row. ag-grid binds its
                // own native row listener, which runs before React's synthetic onClick, so the
                // stopPropagation() in those handlers cannot prevent this navigation — it has to
                // be declined here instead.
                if (p.event?.target?.closest?.("button")) return;
                if (!p.data?.__isSummary && p.data?._id) navigate(`/loan/${p.data._id}`);
              }}
              rowStyle={{ cursor: "pointer" }}
            />
          </div>
        </div>
      )}
      {repayTarget && (
        <RepayDialog
          loan={repayTarget}
          onCancel={() => setRepayTarget(null)}
          onConfirm={confirmRepay}
        />
      )}
    </div>
  );
}
